import { useState, useEffect, useCallback, useRef } from 'react';

// Build-time version is baked in via CRA's env variable substitution
// We read from version.json at startup to get the initial version
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to detect when a new version of the app is available.
 * Polls /version.json and compares against the version loaded at startup.
 * 
 * @returns {{ updateAvailable: boolean, refresh: () => void, currentVersion: string | null }}
 */
const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const initialVersionRef = useRef(null);

  const fetchVersion = useCallback(async () => {
    try {
      // Add cache-busting to ensure we get the latest version.json
      const response = await fetch(`/version.json?t=${Date.now()}`);
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      return data.hash;
    } catch {
      return null;
    }
  }, []);

  // On mount, fetch the initial version
  useEffect(() => {
    const init = async () => {
      const version = await fetchVersion();
      if (version) {
        initialVersionRef.current = version;
        setCurrentVersion(version);
      }
    };
    init();
  }, [fetchVersion]);

  // Poll for updates - depends on currentVersion so it starts after initial fetch completes
  useEffect(() => {
    // Wait until initial version is fetched
    if (!currentVersion) {
      return;
    }

    const checkForUpdate = async () => {
      const latestVersion = await fetchVersion();
      if (latestVersion && latestVersion !== initialVersionRef.current) {
        setUpdateAvailable(true);
      }
    };

    // Check immediately (after a small delay to not block initial render)
    const initialCheck = setTimeout(checkForUpdate, 10000);
    
    // Then poll at interval
    const interval = setInterval(checkForUpdate, POLL_INTERVAL_MS);

    return () => {
      clearTimeout(initialCheck);
      clearInterval(interval);
    };
  }, [fetchVersion, currentVersion]);

  const refresh = useCallback(() => {
    // Navigate to current URL with cache-busting param to force fresh load
    // window.location.reload(true) is deprecated and ignored by modern browsers
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString());
    window.location.href = url.toString();
  }, []);

  return { updateAvailable, refresh, currentVersion };
};

export default useVersionCheck;
